import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import {
  BusinessListItemDto,
  BusinessDetailDto,
  BusinessSummary,
  BusinessSearchResponse,
  CreateBusinessCommand,
  UpdateBusinessCommand,
  AddBusinessAliasCommand,
  AdminUpdateBusinessAddressCommand,
  AdminUpdateBusinessAddressResponse
} from '../../domain/entities/Business';
import { PaginatedResult, PaginationParams } from '../../domain/common/PaginatedResult';
import { UserRole } from '../../domain/value-objects/UserRole';
import { BusinessService } from '../services/BusinessService';
import { BusinessRepository } from '../../infrastructure/repositories/BusinessRepository';
import { apiClient } from '../../infrastructure/api/ApiClient';
import { notificationService } from '../../infrastructure/notifications';
import { queryKeys } from './queryKeys';
import { CACHE_CONFIG } from './queryClient';

/**
 * Service instance (singleton)
 */
const businessRepository = new BusinessRepository(apiClient);
const businessService = new BusinessService(businessRepository);

/**
 * Create business mutation params
 */
interface CreateBusinessParams {
  command: CreateBusinessCommand;
  userRole?: UserRole;
}

/**
 * Update business mutation params
 */
interface UpdateBusinessParams {
  id: string;
  command: UpdateBusinessCommand;
  userRole: UserRole;
  userBusinessId: string | null;
}

/**
 * Deactivate business mutation params
 * Sprint 045: Renamed from DeleteBusinessParams
 */
interface DeactivateBusinessParams {
  id: string;
  userRole: UserRole;
}

/**
 * Reactivate business mutation params
 * Sprint 045: New for reactivating deactivated businesses
 */
interface ReactivateBusinessParams {
  id: string;
  userRole: UserRole;
}

/**
 * Add business alias mutation params
 */
interface AddBusinessAliasParams {
  businessId: string;
  command: AddBusinessAliasCommand;
}

/**
 * Remove business alias mutation params
 */
interface RemoveBusinessAliasParams {
  businessId: string;
  aliasId: string;
}

/**
 * Reprocess business mentions response
 */
interface ReprocessBusinessMentionsResponse {
  message: string;
  totalEvaluated: number;
  deactivated: number;
  reactivated: number;
}

/**
 * Inactive mention item (SESSION_092 - Sprint 011a: Task 11)
 */
export interface InactiveMentionItem {
  id: string;
  redditPostId: string;
  subreddit: string;
  postTitle: string;
  content: string;
  postAuthor: string;
  postCreatedAt: string;
  postUrl: string;
  score: number | null;
  commentCount: number | null;
  extractedBusinessName: string;
  matchConfidence: number | null;
  matchedBusinessId: string | null;
  matchedBusinessName: string | null;
  sentimentScore: number | null;
  sentimentLabel: string | null;
  matchCriteriaStatus: string;
  matchCriteriaLastCheckedAt: string | null;
  reviewStatus: string;
  createdAt: string;
}

/**
 * Get inactive mentions response (SESSION_092 - Sprint 011a: Task 11)
 */
export interface GetInactiveMentionsResponse {
  items: InactiveMentionItem[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  success: boolean;
  message: string;
}

/**
 * Fetch businesses list query hook (admin only)
 * Returns BusinessListItemDto for list display with statistics
 *
 * @param params - Pagination parameters
 * @param userRole - Current user's role
 * @param enabled - Whether the query should run (default: true for admins)
 * @param searchTerm - Optional search term for server-side filtering
 * @param category - Optional category filter
 * @param regionId - Optional region ID filter
 * @param subAreaId - Optional subarea ID filter
 * @param sortBy - Optional sort field (Name, MentionCount, Category, CreatedAt, RegionName)
 * @param sortDescending - Optional sort direction (default: false = ascending)
 */
export const useBusinessesQuery = (
  params?: PaginationParams,
  userRole?: UserRole,
  enabled: boolean = true,
  searchTerm?: string,
  category?: string,
  regionId?: string,
  subAreaId?: string,
  sortBy?: string,
  sortDescending?: boolean,
  isActive?: boolean
): UseQueryResult<PaginatedResult<BusinessListItemDto>, Error> => {
  return useQuery({
    queryKey: queryKeys.businesses.list(params, searchTerm, category, regionId, subAreaId, sortBy, sortDescending, isActive),
    queryFn: async (): Promise<PaginatedResult<BusinessListItemDto>> => {
      return businessRepository.getAll(params, searchTerm, category, regionId, subAreaId, sortBy, sortDescending, isActive);
    },
    staleTime: CACHE_CONFIG.BUSINESSES.staleTime,
    gcTime: CACHE_CONFIG.BUSINESSES.gcTime,
    enabled: enabled && userRole === 'Admin' // Only admins can fetch all businesses
  });
};

/**
 * Fetch single business by ID query hook
 * Returns BusinessDetailDto with full details for detail pages
 *
 * @param id - Business ID
 * @param userRole - Current user's role
 * @param userBusinessId - Current user's business ID (for BusinessOwner access control)
 * @param enabled - Whether the query should run (default: true)
 */
export const useBusinessQuery = (
  id: string,
  userRole: UserRole,
  userBusinessId: string | null,
  enabled: boolean = true
): UseQueryResult<BusinessDetailDto, Error> => {
  return useQuery({
    queryKey: queryKeys.businesses.detail(id),
    queryFn: async (): Promise<BusinessDetailDto> => {
      return businessService.getBusinessById(id, userRole, userBusinessId);
    },
    staleTime: CACHE_CONFIG.BUSINESSES.staleTime,
    gcTime: CACHE_CONFIG.BUSINESSES.gcTime,
    enabled: enabled && !!id // Only run if enabled and ID is provided
  });
};

/**
 * Search businesses query hook (public - for registration)
 * SESSION_137: Added regionId parameter to filter results by region (for company management)
 *
 * @param query - Search query string
 * @param limit - Maximum number of results (default: 10)
 * @param regionId - Optional region GUID to filter results (for company management - only show same-region businesses)
 * @param enabled - Whether the query should run (default: true if query is not empty)
 */
export const useSearchBusinessesQuery = (
  query: string,
  limit: number = 10,
  regionId?: string,
  enabled?: boolean
): UseQueryResult<BusinessSummary[], Error> => {
  const shouldEnable = enabled !== undefined ? enabled : query.length > 0;

  return useQuery({
    queryKey: queryKeys.businesses.search(query, limit, regionId),
    queryFn: async (): Promise<BusinessSummary[]> => {
      return businessService.searchBusinesses(query, limit, regionId);
    },
    staleTime: CACHE_CONFIG.SEARCH.staleTime,
    gcTime: CACHE_CONFIG.SEARCH.gcTime,
    enabled: shouldEnable
  });
};

/**
 * Paginated search businesses query hook (public - for registration with pagination)
 * Sprint 037: Added for registration page with relevance-based sorting and pagination
 *
 * Returns full pagination metadata and relevance-scored results.
 * Exact name matches appear first, followed by partial matches.
 *
 * @param query - Search query string
 * @param pageNumber - Page number (1-indexed, default: 1)
 * @param pageSize - Results per page (default: 10)
 * @param regionId - Optional region GUID to filter results
 * @param enabled - Whether the query should run (default: true if query is not empty)
 */
export const usePaginatedSearchBusinessesQuery = (
  query: string,
  pageNumber: number = 1,
  pageSize: number = 10,
  regionId?: string,
  enabled?: boolean
): UseQueryResult<BusinessSearchResponse, Error> => {
  const shouldEnable = enabled !== undefined ? enabled : query.length > 0;

  return useQuery({
    queryKey: queryKeys.businesses.searchPaginated(query, pageNumber, pageSize, regionId),
    queryFn: async (): Promise<BusinessSearchResponse> => {
      // S_037_C_001: Performance logging for paginated search
      const startTime = performance.now();
      console.log(`[PERF] PaginatedSearchBusinesses START - query: ${query}, page: ${pageNumber}`);

      try {
        const result = await businessRepository.searchWithPagination(query, pageNumber, pageSize, regionId);

        const duration = performance.now() - startTime;
        console.log(`[PERF] PaginatedSearchBusinesses COMPLETE - ${duration.toFixed(0)}ms - query: ${query}, results: ${result.results.length}/${result.totalCount}`);

        return result;
      } catch (error) {
        const duration = performance.now() - startTime;
        console.error(`[PERF] PaginatedSearchBusinesses ERROR - ${duration.toFixed(0)}ms - query: ${query}`, error);
        throw error;
      }
    },
    staleTime: CACHE_CONFIG.SEARCH.staleTime,
    gcTime: CACHE_CONFIG.SEARCH.gcTime,
    enabled: shouldEnable
  });
};

/**
 * Fetch a single business summary by ID (public - for registration URL pre-population)
 *
 * SESSION_178: Added for email marketing workflow.
 * SESSION_276: Fixed to use dedicated public endpoint instead of search workaround.
 * S_032_C_001: Added performance logging to diagnose slow registration pre-population.
 *
 * When a user clicks a link with ?businessId=xxx, this fetches the business for pre-selection.
 * Uses GET /api/businesses/{id}/summary which is [AllowAnonymous] for unauthenticated users.
 *
 * @param businessId - The business ID (GUID string) to fetch
 * @param enabled - Whether the query should run (default: true if businessId is provided)
 */
export const useGetBusinessSummaryByIdQuery = (
  businessId: string | null,
  enabled?: boolean
): UseQueryResult<BusinessSummary | null, Error> => {
  const shouldEnable = enabled !== undefined ? enabled : !!businessId;

  return useQuery({
    queryKey: queryKeys.businesses.summaryById(businessId ?? 'null'),
    queryFn: async (): Promise<BusinessSummary | null> => {
      if (businessId === null || businessId === '') {
        return null;
      }

      // S_032_C_001: Performance logging for registration pre-population
      const startTime = performance.now();
      console.log(`[PERF] GetBusinessSummaryById START - businessId: ${businessId}`);

      try {
        // SESSION_276: Now uses dedicated public endpoint instead of search workaround
        const result = await businessService.getBusinessSummaryById(businessId);

        const duration = performance.now() - startTime;
        console.log(`[PERF] GetBusinessSummaryById COMPLETE - ${duration.toFixed(0)}ms - businessId: ${businessId}, found: ${!!result}`);

        return result;
      } catch (error) {
        const duration = performance.now() - startTime;
        console.error(`[PERF] GetBusinessSummaryById ERROR - ${duration.toFixed(0)}ms - businessId: ${businessId}`, error);
        throw error;
      }
    },
    staleTime: CACHE_CONFIG.BUSINESSES.staleTime,
    gcTime: CACHE_CONFIG.BUSINESSES.gcTime,
    enabled: shouldEnable && !!businessId
  });
};

/**
 * Fetch all businesses for a company by company ID (for fast registration pre-population)
 *
 * SESSION_276: Optimization for email link users.
 * S_032_C_001: Added performance logging to diagnose slow registration pre-population.
 * Fetches directly by companyId (indexed lookup) instead of text search.
 * ~200ms vs 600ms-2s for text-based search.
 *
 * @param companyId - The company ID (GUID string) to fetch businesses for
 * @param enabled - Whether the query should run (default: true if companyId is provided)
 */
export const useGetBusinessesByCompanyIdQuery = (
  companyId: string | null,
  enabled?: boolean
): UseQueryResult<BusinessSummary[], Error> => {
  const shouldEnable = enabled !== undefined ? enabled : !!companyId;

  return useQuery({
    queryKey: queryKeys.businesses.byCompanyId(companyId ?? 'null'),
    queryFn: async (): Promise<BusinessSummary[]> => {
      if (companyId === null || companyId === '') {
        return [];
      }

      // S_032_C_001: Performance logging for registration pre-population
      const startTime = performance.now();
      console.log(`[PERF] GetBusinessesByCompanyId START - companyId: ${companyId}`);

      try {
        const result = await businessService.getBusinessesByCompanyId(companyId);

        const duration = performance.now() - startTime;
        console.log(`[PERF] GetBusinessesByCompanyId COMPLETE - ${duration.toFixed(0)}ms - companyId: ${companyId}, count: ${result.length}`);

        return result;
      } catch (error) {
        const duration = performance.now() - startTime;
        console.error(`[PERF] GetBusinessesByCompanyId ERROR - ${duration.toFixed(0)}ms - companyId: ${companyId}`, error);
        throw error;
      }
    },
    staleTime: CACHE_CONFIG.BUSINESSES.staleTime,
    gcTime: CACHE_CONFIG.BUSINESSES.gcTime,
    enabled: shouldEnable && !!companyId
  });
};

/**
 * Fetch business categories query hook
 *
 * @param enabled - Whether the query should run (default: true)
 */
export const useCategoriesQuery = (
  enabled: boolean = true
): UseQueryResult<string[], Error> => {
  return useQuery({
    queryKey: ['businesses', 'categories'],
    queryFn: async (): Promise<string[]> => {
      return businessRepository.getCategories();
    },
    staleTime: CACHE_CONFIG.BUSINESSES.staleTime,
    gcTime: CACHE_CONFIG.BUSINESSES.gcTime,
    enabled
  });
};

/**
 * Create business mutation hook
 *
 * Creates a new business and invalidates the businesses list cache
 */
export const useCreateBusinessMutation = (): UseMutationResult<void, Error, CreateBusinessParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ command, userRole }: CreateBusinessParams): Promise<void> => {
      await businessService.createBusiness(command, userRole);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate all business list queries to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.businesses.lists() });

      // Show success notification
      notificationService.showSuccess(
        `Business "${variables.command.name}" has been created successfully`,
        'Business Created'
      );
    },
    onError: (error: Error): void => {
      console.error('Create business failed:', error);
    }
  });
};

/**
 * Update business mutation hook
 *
 * Updates an existing business and invalidates related caches
 */
export const useUpdateBusinessMutation = (): UseMutationResult<void, Error, UpdateBusinessParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, command, userRole, userBusinessId }: UpdateBusinessParams): Promise<void> => {
      await businessService.updateBusiness(id, command, userRole, userBusinessId);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate all business list queries to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.businesses.lists() });

      // Invalidate the specific business detail if it's cached
      void queryClient.invalidateQueries({ queryKey: queryKeys.businesses.detail(variables.id) });

      // Show success notification
      notificationService.showSuccess('Business has been updated successfully', 'Business Updated');
    },
    onError: (error: Error): void => {
      console.error('Update business failed:', error);
    }
  });
};

/**
 * Deactivate business mutation hook (admin only)
 * Sprint 045: Sets IsActive = false, reprocesses mentions
 */
export const useDeactivateBusinessMutation = (): UseMutationResult<void, Error, DeactivateBusinessParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, userRole }: DeactivateBusinessParams): Promise<void> => {
      return businessService.deactivateBusiness(id, userRole);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate queries to refetch (business still exists, just inactive)
      void queryClient.invalidateQueries({ queryKey: queryKeys.businesses.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.businesses.detail(variables.id) });

      notificationService.showSuccess('Business has been deactivated and mentions marked inactive', 'Business Deactivated');
    },
    onError: (error: Error): void => {
      console.error('Deactivate business failed:', error);
    }
  });
};

/**
 * Reactivate business mutation hook (admin only)
 * Sprint 045: Sets IsActive = true, reprocesses mentions
 */
export const useReactivateBusinessMutation = (): UseMutationResult<void, Error, ReactivateBusinessParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, userRole }: ReactivateBusinessParams): Promise<void> => {
      return businessService.reactivateBusiness(id, userRole);
    },
    onSuccess: (_data, variables): void => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.businesses.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.businesses.detail(variables.id) });

      notificationService.showSuccess('Business has been reactivated and mentions restored', 'Business Reactivated');
    },
    onError: (error: Error): void => {
      console.error('Reactivate business failed:', error);
      notificationService.showError('Failed to reactivate business. Please try again.', 'Error');
    }
  });
};

/**
 * Admin update business address mutation params
 */
interface AdminUpdateBusinessAddressParams {
  businessId: string;
  command: AdminUpdateBusinessAddressCommand;
}

/**
 * Admin update business address mutation hook
 *
 * Updates address fields on a business's associated Place and invalidates caches
 */
export const useAdminUpdateBusinessAddressMutation = (): UseMutationResult<AdminUpdateBusinessAddressResponse, Error, AdminUpdateBusinessAddressParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, command }: AdminUpdateBusinessAddressParams): Promise<AdminUpdateBusinessAddressResponse> => {
      return businessRepository.updateAdminAddress(businessId, command);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate lists and detail to refetch with updated address
      void queryClient.invalidateQueries({ queryKey: queryKeys.businesses.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.businesses.detail(variables.businessId) });

      notificationService.showSuccess('Business address updated successfully', 'Address Updated');
    },
    onError: (error: Error): void => {
      console.error('Update business address failed:', error);
      notificationService.showError('Failed to update business address. Please try again.', 'Error');
    }
  });
};

/**
 * Add business alias mutation hook
 *
 * Adds an alias to a business and invalidates the business detail cache
 */
export const useAddBusinessAliasMutation = (): UseMutationResult<void, Error, AddBusinessAliasParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, command }: AddBusinessAliasParams): Promise<void> => {
      await apiClient.post(`/api/businesses/${businessId}/aliases`, command);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate the specific business detail to refetch with new alias
      void queryClient.invalidateQueries({ queryKey: queryKeys.businesses.detail(variables.businessId) });

      // Show success notification
      notificationService.showSuccess(`Alias "${variables.command.alias}" has been added successfully`, 'Alias Added');
    },
    onError: (error: Error): void => {
      console.error('Add business alias failed:', error);
      notificationService.showError('Failed to add alias. Please try again.', 'Error');
    }
  });
};

/**
 * Remove business alias mutation hook
 *
 * Removes an alias from a business and invalidates the business detail cache
 */
export const useRemoveBusinessAliasMutation = (): UseMutationResult<void, Error, RemoveBusinessAliasParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, aliasId }: RemoveBusinessAliasParams): Promise<void> => {
      await apiClient.delete(`/api/businesses/${businessId}/aliases/${aliasId}`);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate the specific business detail to refetch without removed alias
      void queryClient.invalidateQueries({ queryKey: queryKeys.businesses.detail(variables.businessId) });

      // Show success notification
      notificationService.showSuccess('Alias has been removed successfully', 'Alias Removed');
    },
    onError: (error: Error): void => {
      console.error('Remove business alias failed:', error);
      notificationService.showError('Failed to remove alias. Please try again.', 'Error');
    }
  });
};

/**
 * Reprocess business mentions mutation hook
 *
 * Reprocesses all mentions for a business based on current matching criteria
 * (SESSION_091 - Sprint 011a: Task 10)
 */
export const useReprocessBusinessMentionsMutation = (): UseMutationResult<ReprocessBusinessMentionsResponse, Error, string, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (businessId: string): Promise<ReprocessBusinessMentionsResponse> => {
      const response = await apiClient.post<ReprocessBusinessMentionsResponse>(
        `/api/businesses/${businessId}/reprocess-mentions`
      );
      return response;
    },
    onSuccess: (data, businessId): void => {
      // Invalidate the specific business detail to refetch mention counts
      void queryClient.invalidateQueries({ queryKey: queryKeys.businesses.detail(businessId) });

      // Invalidate any mentions queries for this business
      void queryClient.invalidateQueries({ queryKey: ['mentions'] });

      // Show success notification with counts
      notificationService.showSuccess(
        `Evaluated ${data.totalEvaluated} mentions: ${data.deactivated} deactivated, ${data.reactivated} reactivated`,
        'Mentions Reprocessed'
      );
    },
    onError: (error: Error): void => {
      console.error('Reprocess business mentions failed:', error);
      notificationService.showError('Failed to reprocess mentions. Please try again.', 'Error');
    }
  });
};

/**
 * Fetch inactive mentions for a business query hook
 *
 * Returns mentions that no longer match the business's current matching criteria
 * (SESSION_092 - Sprint 011a: Task 11)
 *
 * @param businessId - The business ID to fetch inactive mentions for
 * @param pageNumber - Page number (default: 1)
 * @param pageSize - Items per page (default: 10)
 * @param enabled - Whether to enable the query (default: true)
 */
export const useInactiveMentionsQuery = (
  businessId: string,
  pageNumber: number = 1,
  pageSize: number = 10,
  enabled: boolean = true
): UseQueryResult<GetInactiveMentionsResponse, Error> => {
  return useQuery<GetInactiveMentionsResponse, Error>({
    queryKey: ['businesses', businessId, 'mentions', 'inactive', pageNumber, pageSize],
    queryFn: async (): Promise<GetInactiveMentionsResponse> => {
      const response = await apiClient.get<GetInactiveMentionsResponse>(
        `/api/businesses/${businessId}/mentions/inactive`,
        { params: { pageNumber, pageSize } }
      );
      return response;
    },
    enabled: enabled && !!businessId,
    staleTime: 60000, // 1 minute
    retry: 2
  });
};

/**
 * Approve mention by business owner mutation params
 * SESSION_209: Business owner mention approval feature
 */
interface ApproveMentionByOwnerParams {
  businessId: string;
  mentionId: string;
}

/**
 * Reject mention by business owner mutation params
 * SESSION_209: Business owner mention approval feature
 */
interface RejectMentionByOwnerParams {
  businessId: string;
  mentionId: string;
  reason?: string;
}

/**
 * Approve mention by business owner mutation hook
 *
 * Allows a business owner to approve a mention for their business.
 * Updates the mention status to "ApprovedByOwner".
 * (SESSION_209 - Business Owner Mention Approval Feature)
 *
 * Uses: POST /api/businesses/{id}/mentions/{mentionId}/approve
 */
export const useApproveMentionByOwnerMutation = (): UseMutationResult<void, Error, ApproveMentionByOwnerParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, mentionId }: ApproveMentionByOwnerParams): Promise<void> => {
      await apiClient.post(`/api/businesses/${businessId}/mentions/${mentionId}/approve`);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate all mention list queries to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.mentions.lists() });

      // Invalidate the specific business detail if it's cached
      void queryClient.invalidateQueries({ queryKey: queryKeys.businesses.detail(variables.businessId) });

      // Show success notification
      notificationService.showSuccess('Mention has been approved', 'Mention Approved');
    },
    onError: (error: Error): void => {
      console.error('Approve mention by owner failed:', error);
      notificationService.showError('Failed to approve mention. Please try again.', 'Error');
    }
  });
};

/**
 * Reject mention by business owner mutation hook
 *
 * Allows a business owner to reject a mention for their business.
 * Updates the mention status to "RejectedByOwner" and soft-deletes the association.
 * (SESSION_209 - Business Owner Mention Approval Feature)
 *
 * Uses: POST /api/businesses/{id}/mentions/{mentionId}/reject
 */
export const useRejectMentionByOwnerMutation = (): UseMutationResult<void, Error, RejectMentionByOwnerParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, mentionId, reason }: RejectMentionByOwnerParams): Promise<void> => {
      await apiClient.post(`/api/businesses/${businessId}/mentions/${mentionId}/reject`, { reason });
    },
    onSuccess: (_data, variables): void => {
      // Invalidate all mention list queries to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.mentions.lists() });

      // Invalidate the specific business detail if it's cached
      void queryClient.invalidateQueries({ queryKey: queryKeys.businesses.detail(variables.businessId) });

      // Show success notification
      notificationService.showSuccess('Mention has been rejected', 'Mention Rejected');
    },
    onError: (error: Error): void => {
      console.error('Reject mention by owner failed:', error);
      notificationService.showError('Failed to reject mention. Please try again.', 'Error');
    }
  });
};
